import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Utils } from '@app/_helpers/utilities';
import { ConfigurationService } from '@app/_shared/providers/configuration/configuration.service';
import { RetailPermissionResponse } from '@app/app.interface';
import { Observable, of } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { AlgoPermissions, PlanType } from "../../../../../../utProto/webEnums_pb";
import { CurrentPlan, MbPlanResponse, PlansCatalogResponse, PlanSubscriptionStatusResponse, SignedRedirectUrlResponse, SubscribePlanResponse, UserDetails, UserDetailsResponse, WalletBalanceData, WalletBalanceResponse } from './mb-plans.interface';
import { StorageHelper } from '@app/_helpers/storage';

@Injectable({
  providedIn: 'root'
})
export class MbPlansService {
  userDetails: UserDetails;
  currentPlan: CurrentPlan;
  isCurrentPlanOneTime: boolean = false;
  isIntradayAllowed: boolean = false;
  isCarryForwardAllowed: boolean = false;
  walletBalance: WalletBalanceData;

  private $detailRequest: Observable<UserDetails>;
  private $currentPlanRequest: Observable<CurrentPlan>;
  private $plansCatalogRequest: Observable<PlansCatalogResponse>;
  private $subscribePlanRequest: Observable<SubscribePlanResponse>;
  private $plansStatusRequest: Observable<PlanSubscriptionStatusResponse>;

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
  ) { }

  getCurrentPlan(update: boolean = false): Observable<CurrentPlan> {
    if (this.currentPlan && !update) return of(this.currentPlan);
    if (this.$currentPlanRequest) return this.$currentPlanRequest;

    this.$currentPlanRequest = this.http.get<MbPlanResponse>(this.configurationService.applicationUrls.planInfo).pipe(
      share(),
      map(response => {
        if (response && response.data && response.data.plan && response.data.pricing) {
          this.currentPlan = new CurrentPlan(response);
          this.$currentPlanRequest = null;
          this.isCurrentPlanOneTime = (this.currentPlan.plan_type == PlanType.PLANTYPE_ONETIME);

          for (let permission of this.currentPlan.plan.algo_permissions) {
            if (permission === AlgoPermissions.WEBALGOPERMISSION_INTRADAY) {
              this.isIntradayAllowed = true;
            } else if (permission === AlgoPermissions.WEBALGOPERMISSION_CARRYFORWARD) {
              this.isCarryForwardAllowed = true;
            }
          }

          return this.currentPlan;
        }
      })
    );

    return this.$currentPlanRequest;
  }

  getUserDetails(update: boolean = false): Observable<UserDetails> {
    if (this.userDetails && !update) return of(this.userDetails);
    if (this.$detailRequest) { return this.$detailRequest; }

    this.$detailRequest = this.http.get<UserDetailsResponse>(this.configurationService.applicationUrls.details).pipe(
      share(),
      map(response => {
        if (response && response.data && response.data.client) {
          const clientData = { ...response.data.client, client_id: response.data.client_id };
          clientData['dob'] = clientData['dob'] ? Utils.stringToEpoch(clientData['dob']) * 1e3 : null;

          this.userDetails = new UserDetails(clientData);
          this.$detailRequest = null;
          return this.userDetails;
        }
      })
    );

    return this.$detailRequest;
  }

  checkPermissions() {
    const url = this.configurationService.applicationUrls.checkPermissions;
    return this.http.get<RetailPermissionResponse>(url).pipe(
      map(response => response.response)
    );
  }

  getPlansCatalog(): Observable<PlansCatalogResponse> {
    if (this.$plansCatalogRequest) return this.$plansCatalogRequest;

    const url = this.configurationService.applicationUrls.plansCatalog;
    this.$plansCatalogRequest = this.http.get<PlansCatalogResponse>(url).pipe(
      share(),
      map(response => {
        this.$plansCatalogRequest = null;
        return response;
      })
    );

    return this.$plansCatalogRequest;
  }

  getSinglePlanDetails(planId: number): Observable<any> {
    const url = this.configurationService.applicationUrls.plansCatalog + planId;
    return this.http.get<any>(url);
  }

  toggleAutoPay(is_autopay_off: boolean) {
    const url = this.configurationService.applicationUrls.plansToggleAutopay;
    return this.http.put<any>(url, {
      is_auto_pay_off: !is_autopay_off
    }).pipe(map(response => {
      if (response?.data && response?.success) {
        this.currentPlan.is_autopay_off = response.data.is_auto_pay_off;
      }
      return response;
    }));
    
  }

  cancelPlan(): Observable<any> {
    const url = this.configurationService.applicationUrls.plansCancel;
    return this.http.get<any>(url);
  }

  subscribePlan(pricingId: number): Observable<SubscribePlanResponse> {
    if (this.$subscribePlanRequest) return this.$subscribePlanRequest;

    const url = this.configurationService.applicationUrls.plansSubscribe;
    const body = {
      pricing_id: pricingId,
      from_mobile: StorageHelper.fromMobile ?? false
    };
    this.$subscribePlanRequest = this.http.post<SubscribePlanResponse>(url, body).pipe(
      share(),
      map(response => {
        this.$subscribePlanRequest = null;
        return response;
      })
    );
    return this.$subscribePlanRequest;
  }

  getPlanSubscriptionStatus(hostedPageId: string): Observable<PlanSubscriptionStatusResponse> {
    if (this.$plansStatusRequest) return this.$plansStatusRequest;

    const url = this.configurationService.applicationUrls.planSubscriptionStatus + hostedPageId;
    this.$plansStatusRequest = this.http.get<PlanSubscriptionStatusResponse>(url).pipe(
      share(),
      map(response => {
        this.$plansStatusRequest = null;
        return response;
      })
    );

    return this.$plansStatusRequest;
  }

  getSignedRedirectUrl(redirect_endpoint: string): Observable<SignedRedirectUrlResponse> {
    const url = this.configurationService.applicationUrls.signedRedirectUrl;
    return this.http.get<SignedRedirectUrlResponse>(url, {
      params: {
        redirect_endpoint
      }
    });
  }

  getWalletBalance(): Observable<WalletBalanceResponse> {
    const url = this.configurationService.applicationUrls.walletUrl;
    return this.http.get<WalletBalanceResponse>(url);
  }
}
